/**
 * Applies a snapshot zip to the workbench store alone — the light preview's restore path
 * (light-preview plan F2).
 *
 * There is no WebContainer to write through here: the store *is* the workspace, the compiler
 * reads it, and the preview recompiles from the one file event this publishes. Template files
 * stay implicit, because `getMergedPreviewFiles` layers the store over them — including the
 * preview bridge modules, which the template ships, so a snapshot that predates them still
 * previews.
 *
 * Like the WebContainer path, this replaces the previous version's files instead of merging
 * with them: switching versions must not leave a file from the one before behind.
 */

import { workbenchStore } from '../stores/workbench';
import { readSnapshotZipEntries, throwIfAborted } from './snapshotZipEntries';
import { wcPerfAsync } from './workbenchPerf';

export interface StoreSnapshotApplyResult {
  /** Files and assets the snapshot carried. */
  written: number;
  /** Files the store held that this version does not. */
  removed: number;
}

export async function applySnapshotZipToStore(
  blob: Blob,
  signal?: AbortSignal
): Promise<StoreSnapshotApplyResult> {
  return wcPerfAsync('wc:snapshot:applyZipToStore', async () => {
    workbenchStore.restoreInProgress.set(true);
    try {
      throwIfAborted(signal);
      const entries = await readSnapshotZipEntries(blob, signal);
      throwIfAborted(signal);

      const files: Record<string, { content: string; modified?: boolean }> = {};
      const assets: Record<string, { base64: string }> = {};
      const toolWritten: Record<string, string> = {};
      for (const entry of entries) {
        if (entry.isBinary) {
          assets[entry.path] = { base64: entry.base64! };
          continue;
        }
        files[entry.path] = { content: entry.text!, modified: false };
        toolWritten[entry.path] = entry.text!;
      }

      const previous = workbenchStore.files.get();
      const removed = Object.keys(previous).filter((path) => files[path] === undefined).length;

      // What the preview last reported about the workspace described the version being
      // replaced. Leaving it standing let the panel call the restored version "no saved
      // version" for the few hundred ms between this write and the recompiled page.
      workbenchStore.previewShowsTemplatePlaceholder.set(false);

      // One `set` per collection, not one per file: the light preview compiles on every file
      // event, and a per-file restore would queue a compile for each of a hundred writes.
      workbenchStore.files.set(files);
      workbenchStore.assets.set(assets);
      workbenchStore.lastToolWrittenByPath.set(toolWritten);
      workbenchStore.fileConflicts.set({});

      return { written: entries.length, removed };
    } finally {
      workbenchStore.restoreInProgress.set(false);
    }
  });
}

/**
 * Drops every user file, leaving the template the merged view falls back to — what the light
 * preview does when the version this tab was holding turns out to be gone.
 */
export function resetPreviewStoreToTemplate(): void {
  workbenchStore.files.set({});
  workbenchStore.assets.set({});
  workbenchStore.lastToolWrittenByPath.set({});
  workbenchStore.fileConflicts.set({});
}
